import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo, Monogram, IDButton, TAGLINE } from "@/marketing/brand";
import { pixelPageView } from "@/lib/metaPixel";

const LINKS = [
  { to: "/sunday-reset", label: "Sunday Reset" },
  { to: "/life-audit", label: "Life Audit" },
];

const FOOTER_GROUPS = [
  { title: "Start Here", links: [["/sunday-reset", "The Sunday Reset"], ["/life-audit", "The Free Life Audit"]] },
];

export const MarketingLayout = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [pathname]);
  // Meta Pixel PageView — fires only on the public marketing pages (this layout
  // is not used by the admin/console), keeping ad data clean.
  useEffect(() => { pixelPageView(); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="id-surface min-h-screen flex flex-col">
      {/* Header */}
      <header className={`sticky top-0 z-40 transition-colors duration-300 ${scrolled ? "bg-[var(--id-ivory)]/90 backdrop-blur-md border-b border-[var(--id-mist)]" : "bg-transparent"}`}>
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 sm:px-8 py-4">
          <Link to="/" data-testid="marketing-logo-link" aria-label="Intrinsic Daily home">
            <Logo className="h-14 sm:h-16" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`}
                className={({ isActive }) => `id-sans text-[13.5px] tracking-[0.01em] id-link-underline transition-colors ${isActive ? "text-[var(--id-charcoal)]" : "text-[var(--id-charcoal)]/70 hover:text-[var(--id-charcoal)]"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <IDButton to="/dashboard" variant="solid" testid="nav-dashboard-cta">Enter Dashboard</IDButton>
            ) : (
              <IDButton to="/sunday-reset" variant="solid" testid="nav-sunday-reset-cta">Get the Sunday Reset</IDButton>
            )}
          </div>

          <button className="lg:hidden text-[var(--id-charcoal)]" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[var(--id-mist)] bg-[var(--id-ivory)] px-5 py-4" data-testid="mobile-nav-panel">
            <div className="flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} className="id-sans py-2.5 text-[15px] text-[var(--id-charcoal)]/80 border-b border-[var(--id-mist)]/70">{l.label}</NavLink>
              ))}
            </div>
            <div className="mt-4 flex flex-col gap-2">
              {user ? (
                <IDButton to="/dashboard" variant="solid" className="w-full">Enter Dashboard</IDButton>
              ) : (
                <IDButton to="/sunday-reset" variant="solid" className="w-full">Get the Sunday Reset</IDButton>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="mt-24 bg-[var(--id-charcoal)] text-[var(--id-ivory)]">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-16">
          <div className="grid gap-12 md:grid-cols-[1.6fr_1fr]">
            <div>
              <div className="flex items-center gap-3">
                <Monogram className="h-11" />
                <span className="id-serif text-2xl tracking-[0.14em]">INTRINSIC DAILY</span>
              </div>
              <p className="mt-5 id-serif text-xl leading-snug text-[var(--id-taupe)] max-w-xs">{TAGLINE}</p>
            </div>
            {FOOTER_GROUPS.map((g) => (
              <div key={g.title}>
                <p className="id-sans text-[11px] font-semibold uppercase tracking-[0.24em] text-[var(--id-gold)]">{g.title}</p>
                <ul className="mt-5 space-y-3">
                  {g.links.map(([to, label]) => (
                    <li key={to}><Link to={to} className="id-sans text-[14px] text-[var(--id-ivory)]/75 hover:text-[var(--id-ivory)] id-link-underline">{label}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/10 pt-6">
            <p className="id-sans text-xs text-[var(--id-ivory)]/50">© {new Date().getFullYear()} Intrinsic Daily. All rights reserved.</p>
            <p className="id-sans text-xs text-[var(--id-ivory)]/50">A personal operating system for intentional living.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
